import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { z } from 'zod';
import { toast } from 'sonner';
import { ENTITY_TYPE_LABELS, type EntityResponse } from '@vagusx/clients';
import { useCreateEntity, useUpdateEntity } from '@/hooks/use-entities';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { useDiscardGuard } from '@/hooks/use-discard-guard';

const ENTITY_TYPES = Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const formSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  name: z.string().min(1, 'Name is required'),
  npi: z.string().optional(),
  phone: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EntityEditorProps {
  open: boolean;
  entity: EntityResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

/** The create sheet. Editing an existing entity happens inside its detail sheet. */
export function EntityEditor({
  open,
  entity,
  onClose,
  onSuccess,
}: EntityEditorProps): React.ReactElement {
  const discard = useDiscardGuard();
  return (
    <Sheet open={open} onOpenChange={(o) => !o && discard.guard(onClose)}>
      {/* The content mounts on each opening (Radix unmounts it once closed),
          so the form starts fresh and stays visible while the panel slides out. */}
      <SheetContent className="w-full max-w-lg sm:max-w-xl">
        <EntityEditorForm
          entity={entity}
          onClose={() => discard.guard(onClose)}
          onSuccess={onSuccess}
          onDirtyChange={discard.setDirty}
        />
      </SheetContent>
      {discard.dialog}
    </Sheet>
  );
}

function entityToFormValues(entity: EntityResponse | null): FormValues {
  return {
    type: entity?.type ?? '',
    name: entity?.name ?? '',
    npi: entity?.npi ?? '',
    phone: entity?.phone ?? '',
    fax: entity?.fax ?? '',
    email: entity?.email ?? '',
    addressLine1: entity?.addressLine1 ?? '',
    addressLine2: entity?.addressLine2 ?? '',
    city: entity?.city ?? '',
    state: entity?.state ?? '',
    postalCode: entity?.postalCode ?? '',
  };
}

/** Header, form and footer of the entity editor — rendered inside a sheet's content. */
export function EntityEditorForm({
  entity,
  onClose,
  onSuccess,
  onDirtyChange,
}: Omit<EntityEditorProps, 'open'> & {
  /** Lets the hosting sheet guard its X, Escape and Cancel against unsaved changes. */
  onDirtyChange: (isDirty: boolean) => void;
}): React.ReactElement {
  const isEdit = !!entity;
  const createMutation = useCreateEntity();
  const updateMutation = useUpdateEntity();

  // Starts from the entity, so the first frame already shows its values.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: entityToFormValues(entity),
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    form.reset(entityToFormValues(entity));
  }, [entity, form]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = async (values: FormValues): Promise<void> => {
    const data = {
      ...values,
      email: values.email || null,
      npi: values.npi || null,
      phone: values.phone || null,
      fax: values.fax || null,
      addressLine1: values.addressLine1 || null,
      addressLine2: values.addressLine2 || null,
      city: values.city || null,
      state: values.state || null,
      postalCode: values.postalCode || null,
    };

    if (isEdit) {
      await updateMutation.mutateAsync({ id: entity.id, data });
      toast.success('Entity updated');
    } else {
      await createMutation.mutateAsync(data);
      toast.success('Entity created');
    }
    // Saved, so nothing left to lose: leave without asking.
    onDirtyChange(false);
    onSuccess();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <SheetHeader className="px-6">
        <SheetTitle>{isEdit ? 'Edit Entity' : 'Add Entity'}</SheetTitle>
        <SheetDescription>
          {isEdit ? 'Update entity information' : 'Add a new external entity'}
        </SheetDescription>
      </SheetHeader>

      <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
        <SheetBody className="px-6 pb-6 space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={form.watch('type')}
              onValueChange={(v) => form.setValue('type', v, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.type && (
              <p className="text-sm text-destructive">{form.formState.errors.type.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Name</Label>
            <Input {...form.register('name')} placeholder="Entity name" />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>NPI</Label>
            <Input {...form.register('npi')} placeholder="NPI number" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input {...form.register('phone')} placeholder="Phone" />
            </div>
            <div className="space-y-2">
              <Label>Fax</Label>
              <Input {...form.register('fax')} placeholder="Fax" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input {...form.register('email')} type="email" placeholder="Email address" />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Address Line 1</Label>
            <Input {...form.register('addressLine1')} placeholder="Street address" />
          </div>

          <div className="space-y-2">
            <Label>Address Line 2</Label>
            <Input {...form.register('addressLine2')} placeholder="Suite, apt, etc." />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input {...form.register('city')} placeholder="City" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input {...form.register('state')} placeholder="State" />
            </div>
            <div className="space-y-2">
              <Label>Zip</Label>
              <Input {...form.register('postalCode')} placeholder="Zip" />
            </div>
          </div>
        </SheetBody>

        <SheetActions className="px-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {isEdit ? (
            <SaveButton type="submit" isPending={isPending} isEdit />
          ) : (
            <Button type="submit" disabled={isPending}>
              Create
            </Button>
          )}
        </SheetActions>
      </form>
    </>
  );
}
